import type {
  AiConnection,
  BookingEdit,
  ChangePage,
  CodeRedeemed,
  CurrencySet,
  DatesHint,
  DeletedTrip,
  DeletionPreview,
  ExportState,
  FeedbackSent,
  JoinPreview,
  Me,
  MyBilling,
  NotificationSettings,
  RefundAsked,
  RefundRequestState,
  SharedView,
  SignedOutElsewhere,
  StartedTrip,
  TermsAsked,
  Tip,
  TripItems,
  TripList,
  TripName,
  TripSharing,
  TripView,
  TripWeather,
  When,
} from "../../src/web/types.ts";
import { noteFailure } from "./feedback.ts";
import { meOf } from "./me.ts";
import { forgetKept, noteAnswer } from "./offline.ts";
import { pushSubscription } from "./push.ts";
import { forgetTrips } from "./reopen.ts";

export type * from "../../src/web/types.ts";

// The device being told again that it's mine, while it is (api.keepDevice).
let keeping: Promise<unknown> = Promise.resolve();

// A travel document as its form sends it (Documents.tsx; src/web/api.ts, its routes): whole, status and expiry null
// for none, who sees its details "everyone" or person ids (none: only its owner).
export interface DocumentDraft {
  kind: string;
  title: string;
  status: string | null;
  expires: string | null;
  details: Record<string, string | number | boolean>;
  shared_with: "everyone" | string[];
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    // The rest of Tripmo's answer: a deleted trip's comes with the trip (410, `trip`).
    readonly data: Record<string, unknown> = {},
    // The request's id (x-request-id), for Report this (features/feedback.md).
    readonly requestId: string | null = null,
  ) {
    super(message);
  }
}

// The trip a refusal says was deleted (its page, its join link), if that's what it says.
export function deletedTrip(error: unknown): DeletedTrip | null {
  if (!(error instanceof ApiError) || error.status !== 410) return null;
  return (error.data["trip"] as DeletedTrip | undefined) ?? null;
}

// 16 or older, and the Terms, once (src/auth/terms.ts): what a refusal says the screen asks, if that's what it says.
export function termsAsked(error: unknown): TermsAsked | null {
  if (!(error instanceof ApiError) || error.status !== 403 || error.data["error"] !== "terms_needed") return null;
  return (error.data["terms"] as TermsAsked | undefined) ?? null;
}
// Said to the page when any call is refused that way (the Terms changed while it was open): App.tsx shows the
// screen.
export const TERMS_NEEDED_EVENT = "tripmo:terms";
// How many times this page has agreed to the Terms. A call refused as needing them that was already on its way when
// they were agreed to was answered before (the screen's own loads, a live stream asking why it was refused), so its
// refusal doesn't bring the screen back (2026-10-25: one answering just after Continue showed the screen again).
let agreements = 0;
// A request's id, as Tripmo (or, where the box didn't answer, Cloudflare) gave it.
const idOf = (res: Response) => res.headers.get("x-request-id") ?? res.headers.get("cf-ray");
// `since`: `agreements` as the call was sent.
function refused(status: number, body: { message?: string }, since: number, res?: Response): ApiError {
  const error = new ApiError(status, body.message ?? `Tripmo answered ${status}`, body, res ? idOf(res) : null);
  const asked = termsAsked(error);
  if (asked && since === agreements) dispatchEvent(new CustomEvent(TERMS_NEEDED_EVENT, { detail: asked }));
  return error;
}

// A live stream refused (event-stream.ts): an EventSource can't read why, so a plain request asks. Refused with
// `terms_needed` (the Terms changed while the page was open), it brings the screen up as any refusal does; any
// other answer changes nothing, and the stream goes on trying.
export async function askWhyRefused(): Promise<void> {
  const since = agreements;
  const res = await fetch("/api/me", { credentials: "same-origin", headers: { accept: "application/json" } }).catch(
    () => null,
  );
  if (res?.status === 403) refused(403, (await res.json().catch(() => ({}))) as { message?: string }, since);
}

// Without a connection, a change can't go through (and what the phone hasn't kept can't be shown).
const NO_SIGNAL = "No signal. This needs a connection: try again when you're back online.";
// Every request that fails is noted, for what helps find a report (feedback.ts).
async function call(url: string, init: RequestInit): Promise<Response> {
  let res: Response;
  try {
    res = await fetch(url, init);
  } catch {
    throw new ApiError(0, NO_SIGNAL);
  }
  if (!res.ok) noteFailure(res, url);
  return res;
}

// Tripmo's answer, read whole (features/opening.md, "A page survives a deploy under it"). An answer said to have gone
// through (2xx) whose body doesn't read as JSON (cut off on the way, or something answering in Tripmo's place) is no
// answer: refused as Tripmo not answering (failure.ts), never handed on as an empty `{}` for the page to draw from (one
// taken for who I am crashed the trip page on plan-dev, 2026-11-03). A refusal with no words of its own says its
// status.
const UNREADABLE = "Tripmo's answer didn't come through. Try again in a few seconds.";
async function answerOf(res: Response, since: number): Promise<unknown> {
  const body: unknown = await res.json().catch(() => undefined);
  const read = typeof body === "object" && body !== null ? (body as { message?: string }) : undefined;
  if (!res.ok) throw refused(res.status, read ?? {}, since, res);
  if (!read) {
    noteFailure(res, res.url);
    throw new ApiError(res.status, UNREADABLE, { error: "unreadable" }, idOf(res));
  }
  return read;
}

// GETs a JSON API route. Signed out (or the session ended), it goes to sign-in and comes back here.
// An answer the phone kept says so (offline.ts), when it was kept, and why it was used (web/sw/sw.ts).
async function get<T>(path: string): Promise<T> {
  const since = agreements;
  const res = await call(`/api${path}`, { credentials: "same-origin", headers: { accept: "application/json" } });
  if (res.ok) noteAnswer(path, res.headers.get("x-tripmo-kept-at"), res.headers.get("x-tripmo-kept-because"));
  if (res.status === 401) {
    location.assign(`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`);
    return new Promise<T>(() => {});
  }
  return (await answerOf(res, since)) as T;
}

// POSTs a change. Failures come back as the store's own message ("only the trip's owner can …").
async function post<T>(path: string, body: unknown = {}): Promise<T> {
  const since = agreements;
  const res = await call(`/api${path}`, {
    method: "POST",
    credentials: "same-origin",
    headers: { "content-type": "application/json", accept: "application/json" },
    body: JSON.stringify(body),
  });
  if (res.status === 401) {
    location.assign(`/sign-in?next=${encodeURIComponent(location.pathname + location.search)}`);
    return new Promise<T>(() => {});
  }
  return (await answerOf(res, since)) as T;
}

// GETs the view's own API (features/sharing.md): signed out is no reason to go to sign-in (a link shared with anyone
// opens signed out), so every refusal comes back as it is, for the view's page to say (ViewPage.tsx).
async function viewGet<T>(path: string): Promise<T> {
  const since = agreements;
  const res = await call(`/api/view${path}`, { credentials: "same-origin", headers: { accept: "application/json" } });
  return (await answerOf(res, since)) as T;
}

const at = (trip: string) => `/trips/${encodeURIComponent(trip)}`;
const sharingAt = (trip: string) => `${at(trip)}/share`;

export const api = {
  // Whole whatever came (me.ts); with no me in it, Tripmo not answering.
  me: async (): Promise<Me> => {
    const me = meOf(await get<unknown>("/me"));
    if (!me) throw new ApiError(200, UNREADABLE, { error: "unreadable" });
    return me;
  },
  // A shared trip by its view link's code, as a viewer sees it, and its days' weather (features/sharing.md).
  view: (code: string) => viewGet<SharedView>(`/${encodeURIComponent(code)}`),
  viewWeather: (code: string) => viewGet<TripWeather>(`/${encodeURIComponent(code)}/weather`),
  // Share to view, in Trip settings (the owner's): on, changed (who, what, the list), the link replaced, stopped;
  // and each member's own choice for their name, photo and ratings. Each answers the trip's sharing as it is after.
  shareOn: (trip: string) => post<TripSharing>(`${sharingAt(trip)}/on`),
  shareChange: (
    trip: string,
    change: { who?: TripSharing["who"]; shares?: TripSharing["shares"]; emails?: string[] },
  ) => post<TripSharing>(sharingAt(trip), change),
  shareReplace: (trip: string) => post<TripSharing>(`${sharingAt(trip)}/replace`),
  shareStop: (trip: string) => post<TripSharing>(`${sharingAt(trip)}/stop`),
  shareMine: (trip: string, show: boolean) => post<TripSharing>(`${sharingAt(trip)}/mine`, { show }),
  ais: () => get<{ ais: AiConnection[] }>("/me/ais"),
  // Disconnect an AI, by the names its registrations gave (null: none) that Your AIs shows as one: my AIs as they are
  // after.
  disconnectAi: (names: (string | null)[]) => post<{ ais: AiConnection[] }>("/me/ais/disconnect", { names }),
  // Sign out of other devices. This browser's notifications stay on: it says which device is its own, once Tripmo
  // has been told it from this sign-in.
  async signOutOthers(): Promise<SignedOutElsewhere> {
    await keeping;
    const subscription = await pushSubscription().catch(() => null);
    return post<SignedOutElsewhere>("/me/sign-out-others", subscription ? { endpoint: subscription.endpoint } : {});
  },
  // Something getting started showed me once (a tip inside a trip too), put away for good on every device.
  putTipAway: (tip: Tip) => post<{ tip: Tip }>("/me/tips", { tip }),
  // My device's time zone, which due-date reminders and the weekly round-up go at 9:00 in (zone.ts): said in
  // the background, so a refusal (signed out meanwhile, no signal) changes nothing on the page; it's said again
  // on the next open.
  async setTimeZone(zone: string): Promise<boolean> {
    const res = await call("/api/me/time-zone", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ time_zone: zone }),
    });
    return res.ok;
  },
  // My trips by name, for the trip menu.
  tripNames: () => get<{ trips: TripName[] }>("/me/trips"),
  // My trips, those I deleted that I can still restore (Recently deleted), and my face's colour.
  trips: () => get<TripList>("/trips"),
  // Starting a trip here (its name, optional dates): from a Trip Pass's placeholder, or New trip on a plan.
  startTripFromPass: (pass: string, trip: { title: string; dates?: When }) =>
    post<StartedTrip>(`/passes/${encodeURIComponent(pass)}/trip`, trip),
  newTrip: (trip: { title: string; dates?: When }) => post<StartedTrip>("/trips", trip),
  // A code for a free Trip Pass (features/paying.md, "Free Trip Passes from codes").
  redeem: (code: string) => post<CodeRedeemed>("/redeem", { code }),
  // Profile's Billing.
  billing: () => get<MyBilling>("/me/billing"),
  // Asking for a refund of a pass, spots or a plan of mine (the server checks it at once), and taking
  // back one still open.
  requestRefund: (target: { type: RefundRequestState["target_type"]; id: string }, reason?: string) =>
    post<RefundAsked>("/me/refund-requests", {
      target_type: target.type,
      target_id: target.id,
      ...(reason ? { reason } : {}),
    }),
  withdrawRefund: (id: string) =>
    post<{ request: RefundRequestState }>(`/me/refund-requests/${encodeURIComponent(id)}/withdraw`),
  trip: (id: string) => get<TripView>(`/trips/${encodeURIComponent(id)}`),
  items: (id: string) => get<TripItems>(`/trips/${encodeURIComponent(id)}/items`),
  // Each day's weather for where we are (features/weather.md).
  weather: (id: string) => get<TripWeather>(`/trips/${encodeURIComponent(id)}/weather`),
  changes: (trip: string, cursor?: string) =>
    get<ChangePage>(`${at(trip)}/changes${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ""}`),
  // The changes that touched one record, newest first: a deleted item's deletion.
  changesTo: (trip: string, record: string) =>
    get<ChangePage>(`${at(trip)}/changes?record=${encodeURIComponent(record)}`),
  setPhoto: async (photo: Blob): Promise<{ photo: string | null }> => {
    const res = await call("/api/me/photo", {
      method: "PUT",
      credentials: "same-origin",
      headers: { "content-type": photo.type || "image/jpeg", accept: "application/json" },
      body: photo,
    });
    const data = (await res.json().catch(() => ({}))) as { message?: string; photo?: string | null };
    if (!res.ok) throw new ApiError(res.status, data.message ?? `Tripmo answered ${res.status}`, data, idOf(res));
    return { photo: data.photo ?? null };
  },
  removePhoto: () => post<{ photo: null }>("/me/photo/remove"),
  // Send feedback and Report this (features/feedback.md): the form as one multipart body, its screenshot a file in it.
  // Signed out meanwhile, it says so rather than leave for sign-in, so what was written isn't lost.
  sendFeedback: async (form: FormData): Promise<FeedbackSent> => {
    const since = agreements;
    const res = await call("/api/feedback", {
      method: "POST",
      credentials: "same-origin",
      headers: { accept: "application/json" },
      body: form,
    });
    const data = (await res.json().catch(() => ({}))) as { message?: string };
    if (res.status === 401) throw new ApiError(401, "You're signed out. Sign in again in another tab, then send it.");
    if (!res.ok) throw refused(res.status, data, since, res);
    return data as FeedbackSent;
  },
  setName: (name: string) => post<{ name: string }>("/me/name", { name }),
  // My currency (the profile's Your currency): my account's, for every trip; null puts back none (Undo at once).
  setCurrency: (currency: string | null) => post<CurrencySet>("/me/currency", { currency }),
  share: (trip: string, on: boolean) => post(`${at(trip)}/sharing`, { on }),
  sendLocation: (trip: string, point: { lat: number; lng: number; accuracy_m?: number }) =>
    post<{ at: string }>(`${at(trip)}/location`, point),
  // A file from this device, on an item or my document: its bytes as the body, its name in a header.
  attachFile: async (trip: string, on: string, file: File, as: "file" | "photo" = "file"): Promise<{ id: string }> => {
    const res = await call(`/api${at(trip)}/files?on=${encodeURIComponent(on)}${as === "photo" ? "&as=photo" : ""}`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "x-file-name": encodeURIComponent(file.name), accept: "application/json" },
      body: file,
    });
    const data = (await res.json().catch(() => ({}))) as { id?: string; message?: string };
    if (!res.ok) throw new ApiError(res.status, data.message ?? `Tripmo answered ${res.status}`, data, idOf(res));
    return { id: data.id as string };
  },
  removeFile: (trip: string, file: string) => post(`${at(trip)}/files/${encodeURIComponent(file)}/delete`),
  addNote: (trip: string, on: string, text: string) => post<{ id: string }>(`${at(trip)}/notes`, { on, text }),
  // My travel documents, in the app only (Documents.tsx): added, changed over the version read (refused with 409 when
  // it changed meanwhile), and removed; each its change, for Undo.
  addDocument: (trip: string, doc: DocumentDraft) => post<{ id: string; change: string }>(`${at(trip)}/documents`, doc),
  changeDocument: (trip: string, id: string, doc: DocumentDraft & { version: number }) =>
    post<{ change: string; version: number }>(`${at(trip)}/documents/${encodeURIComponent(id)}`, doc),
  removeDocument: (trip: string, id: string) =>
    post<{ change: string }>(`${at(trip)}/documents/${encodeURIComponent(id)}/delete`),
  // My diet and mobility in a trip (Trip settings, Diet and mobility): each given set, or with null taken off.
  setNeeds: (trip: string, needs: { diet?: string | null; mobility?: string | null }) =>
    post<{ change: string | null }>(`${at(trip)}/profile`, needs),
  editNote: (trip: string, note: string, text: string) =>
    post(`${at(trip)}/notes/${encodeURIComponent(note)}`, { text }),
  deleteNote: (trip: string, note: string) => post(`${at(trip)}/notes/${encodeURIComponent(note)}/delete`),
  // My rating of an item; null takes it back. The change that saved it (none when nothing changed), for
  // the deck's Undo.
  rate: (trip: string, item: string, tier: string | null) =>
    post<{ change: string | null }>(`${at(trip)}/interest`, { item, tier }),
  // A to-do, changed on the web one thing at a time: the change, for Undo.
  editTodo: (
    trip: string,
    item: string,
    edit: { done: boolean } | { owner: string | null } | { due: string | null } | { important: boolean },
  ) => post<{ change: string; version: number }>(`${at(trip)}/todos/${encodeURIComponent(item)}`, edit),
  // Today's one tap (Today.tsx): done or back, on today (the device's date) or off its day, missed or back in the
  // plan.
  today: (trip: string, item: string, edit: { done: boolean } | { day: string | null } | { missed: boolean }) =>
    post<{ change: string | null; version: number }>(`${at(trip)}/today/${encodeURIComponent(item)}`, edit),
  // A to-do added on the web (AddTodo.tsx): for nobody yet or someone in the trip, due or not, important or not, and
  // about a thing in the trip from its card. The change, for Undo, and the new to-do's id.
  addTodo: (
    trip: string,
    todo: { title: string; owner: string | null; due: string | null; important: boolean; about: string | null },
  ) => post<{ change: string; id: string }>(`${at(trip)}/todos`, todo),
  removeTodo: (trip: string, item: string) =>
    post<{ change: string }>(`${at(trip)}/todos/${encodeURIComponent(item)}/remove`),
  // A reminder still to come, changed on the web (Reminders.tsx): only what changed, its words, its local time (in its
  // own time zone) or who it's for. Refused as it changed or was sent meanwhile, the refusal carries it as it is now
  // (`reminder`). The change (none when nothing changed), for Undo.
  editReminder: (
    trip: string,
    reminder: string,
    edit: { version: number; text?: string; at?: string; to?: "everyone" | string[] },
  ) => post<{ change: string | null; version: number }>(`${at(trip)}/reminders/${encodeURIComponent(reminder)}`, edit),
  // A reminder removed on the web, over the version read; refused as for a change. The change, for Undo.
  removeReminder: (trip: string, reminder: string, version: number) =>
    post<{ change: string }>(`${at(trip)}/reminders/${encodeURIComponent(reminder)}/delete`, { version }),
  // A stay's city; null takes it off. The change (none when nothing changed), for Undo.
  setCity: (trip: string, item: string, city: string | null) =>
    post<{ change: string | null; version: number }>(`${at(trip)}/stays/${encodeURIComponent(item)}/city`, { city }),
  // A booking recorded from its card (BookingForm.tsx). Refused as someone changed it meanwhile, the refusal
  // carries it as it is now (`item`).
  book: (trip: string, item: string, edit: BookingEdit) =>
    post<{ change: string | null; version: number }>(`${at(trip)}/bookings/${encodeURIComponent(item)}`, edit),
  // Not booked after all (BookingForm.tsx): a booking marked by mistake, back to needing booking, its fields kept.
  // Refused as someone changed it meanwhile, the refusal carries it as it is now (`item`).
  notBooked: (trip: string, item: string, version: number) =>
    post<{ change: string; version: number }>(`${at(trip)}/bookings/${encodeURIComponent(item)}/not-booked`, {
      version,
    }),
  // Remove from trip (RemoveFromTrip.tsx): it out of the plan for everyone, with a choice's options and the to-dos
  // about it, over the version read. Refused as someone changed it meanwhile, the refusal carries it as it is now
  // (`item`). The change, for Undo, and every item it took out.
  removeItem: (trip: string, item: string, version: number) =>
    post<{ change: string; removed: string[] }>(`${at(trip)}/items/${encodeURIComponent(item)}/remove`, { version }),
  // Plan for a day (PlanDay.tsx): a place or goal on a day (a date, or "day N" while the trip has none), at a time in
  // its time zone if given, or off its day (null). Refused as someone changed it meanwhile, the refusal carries it as
  // it is now (`item`).
  planDay: (trip: string, item: string, edit: { version: number; day: string | null; time?: string; tz?: string }) =>
    post<{ change: string | null; version: number }>(`${at(trip)}/day/${encodeURIComponent(item)}`, edit),
  // My answer to a question (Asks.tsx): a choice, a line, or both; the whole answer as it is to be. Refused as the
  // question changed meanwhile, the refusal carries it as it is now (`ask`).
  answer: (trip: string, ask: string, answer: { version: number; choice: string | null; text: string | null }) =>
    post<{ change: string | null }>(`${at(trip)}/asks/${encodeURIComponent(ask)}/answer`, answer),
  // My answer taken back while the question's open: it waits on me again. The change (none with nothing to take back).
  takeBackAnswer: (trip: string, ask: string, version: number) =>
    post<{ change: string | null }>(`${at(trip)}/asks/${encodeURIComponent(ask)}/answer/delete`, { version }),
  // Closing a question I asked (Asks.tsx). Refused as the question changed meanwhile, the refusal carries it as it is
  // now (`ask`). The change (none when it was closed already), for Undo.
  closeAsk: (trip: string, ask: string, version: number) =>
    post<{ change: string | null }>(`${at(trip)}/asks/${encodeURIComponent(ask)}/close`, { version }),
  // The trip's name and dates (TripDetails.tsx); dates null for none. What the new dates did, the write's hints.
  // Refused as someone changed the trip meanwhile, the refusal carries it as it is now (`trip`).
  tripDetails: (trip: string, edit: { version: number; title: string; dates: When | null }) =>
    post<{ change: string | null; version: number; hints: DatesHint[] }>(`${at(trip)}/details`, edit),
  // A payment added to a card (`add`: who, how much in what, how), or one of mine taken off it (`remove`: its place in
  // the list as read); a charge later added (`charge`: whose card, how much, which, the day), one on my card taken off
  // (`remove_charge`), or one confirmed as paid (`confirm`, into paid). Refused as someone changed it meanwhile, the
  // refusal carries it as it is now (`item`).
  pay: (
    trip: string,
    item: string,
    edit:
      | { version: number; add: { by: string; amount: number; currency: string; method?: string } }
      | { version: number; remove: number }
      | { version: number; charge: { by: string; amount: number; currency: string; method?: string; on: string } }
      | { version: number; remove_charge: number }
      | { version: number; confirm: number },
  ) => post<{ change: string; version: number }>(`${at(trip)}/paid/${encodeURIComponent(item)}`, edit),
  // A link saved to the trip's inbox (SaveLink.tsx, ShareLink.tsx), with a note if given; or, with `items`, added on a
  // card, about that item (CardLinks.tsx).
  saveLink: (trip: string, link: { url: string; note?: string; items?: string[] }) =>
    post<{ change: string; id: string }>(`${at(trip)}/links`, link),
  // A saved link changed (Ideas' Edit and About, a card's links' Edit): its address, its note (null: none), what it's
  // about (none: back to the inbox). Refused as someone changed it meanwhile, the refusal carries it as it is now
  // (`link`). The change (none when nothing changed), for Undo.
  editLink: (
    trip: string,
    link: string,
    edit: { version: number; url?: string; note?: string | null; items?: string[] },
  ) => post<{ change: string | null; version: number }>(`${at(trip)}/links/${encodeURIComponent(link)}`, edit),
  // One of a card's links (a pill: its address everywhere it's kept on the card, its own fields and the saved posts
  // about it at that address, as `saved`'s versions) changed to `to`, or taken off (`to` null); a saved post's note
  // and what it's about with it. Refused as someone changed it meanwhile, the refusal carries the card as it is now
  // (`item`).
  editCardLink: (
    trip: string,
    item: string,
    edit: {
      version: number;
      from: string;
      to: string | null;
      saved: Record<string, number>;
      note?: string | null;
      items?: string[];
    },
  ) => post<{ change: string | null; version: number }>(`${at(trip)}/items/${encodeURIComponent(item)}/links`, edit),
  // A link removed (from Ideas' inbox or a place's card), for anyone in the trip: the change, for Undo.
  removeLink: (trip: string, link: string) =>
    post<{ change: string }>(`${at(trip)}/links/${encodeURIComponent(link)}/delete`),
  // A web photo this page couldn't load: Tripmo checks it again itself.
  photoBroken: (trip: string, item: string, url: string) =>
    post<{ broken: boolean }>(`${at(trip)}/photos/broken`, { item, url }),
  undo: (trip: string, change: string) =>
    post<{ change: string }>(`${at(trip)}/changes/${encodeURIComponent(change)}/undo`),
  join: (code: string) => get<JoinPreview>(`/join/${encodeURIComponent(code)}`),
  askToJoin: (code: string) => post<{ trip: string; status: string }>(`/join/${encodeURIComponent(code)}`),
  // Taking back my request to join (the join page, Your trips' card).
  withdrawRequest: (trip: string) => post(`${at(trip)}/request/withdraw`),
  // Turn-downs said (Your trips, the join page): not said again.
  turnDownsSeen: (trips: string[]) => post("/me/turned-down/seen", { trips }),
  letIn: (trip: string, member: string) => post(`${at(trip)}/members/${encodeURIComponent(member)}/let-in`),
  remove: (trip: string, member: string) => post(`${at(trip)}/members/${encodeURIComponent(member)}/remove`),
  // Taking over a trip (DESIGN.md, Member, "Taking over a trip"): the owner offers it to a member or takes the offer
  // back; the member it's offered to takes it over (402, `kind: "takeover"`, while something is to buy first) or turns
  // it down.
  offerTrip: (trip: string, member: string) => post(`${at(trip)}/owner`, { member }),
  withdrawOffer: (trip: string) => post(`${at(trip)}/offer/withdraw`),
  takeOver: (trip: string) => post(`${at(trip)}/offer/accept`),
  declineOffer: (trip: string) => post(`${at(trip)}/offer/decline`),
  putAwayInvite: (trip: string, how: "for_good" | "week") => post(`${at(trip)}/invite`, { put_away: how }),
  newJoinLink: (trip: string) => post<{ join_link: string }>(`${at(trip)}/join-link`),
  // Deleting the trip (its owner, from Trip settings), and bringing it back within 30 days.
  deleteTrip: (trip: string) => post<{ change: string; restorable_until: string }>(`${at(trip)}/delete`),
  restoreTrip: (trip: string) => post<{ change: string | null }>(`${at(trip)}/restore`),
  notifications: () => get<NotificationSettings>("/notifications"),
  // Tripmo's key as it is now, to check this device's subscription against: null when notifications
  // aren't set up, or when the answer is one the phone kept (no signal), which may be from before the key
  // pair was replaced.
  async pushKey(): Promise<string | null> {
    const res = await call("/api/notifications", {
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });
    if (!res.ok || res.headers.has("x-tripmo-kept-at")) return null;
    return ((await res.json()) as NotificationSettings).key;
  },
  // This device's subscription, and the one it replaces (made with Tripmo's old key), which Tripmo forgets.
  addDevice: (subscription: PushSubscriptionJSON, replaces: string | null = null) =>
    post("/notifications/devices", { ...subscription, ...(replaces ? { replaces } : {}) }),
  // Opening Tripmo tells it this device again, and coming back does when it subscribed again
  // (useKeepDevice); signing out waits for those first, or the device would be told again after it was
  // removed and keep getting my notifications.
  keepDevice(device: Promise<{ subscription: PushSubscriptionJSON; replaces: string | null } | null>) {
    const told = device.then((d) => d && api.addDevice(d.subscription, d.replaces)).catch(() => {});
    keeping = Promise.all([keeping, told]);
    return keeping;
  },
  removeDevice: (endpoint: string) => post("/notifications/devices/remove", { endpoint }),
  testDevice: (subscription: PushSubscriptionJSON, device: "phone" | "computer") =>
    post<{ sent: true }>("/notifications/test", { ...subscription, device }),
  setKind: (kind: keyof NotificationSettings["kinds"], on: boolean) =>
    post<{ kinds: NotificationSettings["kinds"] }>("/notifications/kinds", { kind, on }),
  // Download your data and Delete my account (features/privacy-and-data.md).
  dataExport: () => get<ExportState>("/me/data/export"),
  requestExport: () => post<ExportState>("/me/data/export"),
  deletionPreview: () => get<DeletionPreview>("/me/deletion"),
  // Deleted and signed out everywhere: this browser forgets my trips and notifications as a sign-out does,
  // then says what happens next.
  async deleteAccount() {
    const { finishes_at } = await post<{ finishes_at: string }>("/me/delete", { confirm: true });
    await forgetKept();
    forgetTrips();
    const subscription = await pushSubscription().catch(() => null);
    await subscription?.unsubscribe().catch(() => {});
    location.assign(`/account-deleted?until=${encodeURIComponent(finishes_at.slice(0, 10))}`);
  },
  // The screen after signing up (BeforeWeStart.tsx): both ticked, for the Terms it showed.
  async agreeToTerms(version: string) {
    const agreed = await post<{ terms_accepted_at: string; terms_version: string }>("/me/terms", {
      sixteen_or_older: true,
      agree: true,
      version,
    });
    agreements += 1;
    return agreed;
  },
  // "I'm under 16": the account is removed at once and signed out; this browser forgets what it kept, as signing out
  // does, and the page says why.
  async removeUnder16() {
    await post<{ removed: true }>("/me/under-16", { confirm: true });
    await forgetKept();
    forgetTrips();
    const subscription = await pushSubscription().catch(() => null);
    await subscription?.unsubscribe().catch(() => {});
    location.assign("/account-removed");
  },
  // An AI's authorization that waited for the screen (Better Auth's signed request, in /welcome's address): on to
  // its allow page, or straight back to the AI when it was allowed before. Where the browser goes next.
  async continueAuthorization(oauthQuery: string): Promise<string> {
    const res = await call("/api/auth/oauth2/continue", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json", accept: "application/json" },
      body: JSON.stringify({ created: true, oauth_query: oauthQuery }),
    });
    const data = (await res.json().catch(() => ({}))) as { url?: unknown };
    if (!res.ok || typeof data.url !== "string")
      throw new ApiError(
        res.status,
        "You're all set. This sign-in from your AI has expired: start connecting from your AI again.",
        data as Record<string, unknown>,
      );
    return data.url;
  },
  // Signing out, then on to `to` (Your trips' page by default; a view link's sign-in, to use another account).
  async signOut(to = "/") {
    await keeping;
    await forgetKept();
    forgetTrips();
    // A shared computer stops getting my notifications.
    const subscription = await pushSubscription().catch(() => null);
    if (subscription) {
      await this.removeDevice(subscription.endpoint).catch(() => {});
      await subscription.unsubscribe().catch(() => {});
    }
    await call("/api/auth/sign-out", {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: "{}",
    });
    location.assign(to);
  },
};
